import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { GSDevTools } from 'gsap/GSDevTools';
gsap.registerPlugin(ScrollTrigger, SplitText, GSDevTools);
import tippy from 'tippy.js';
import { debounce, requestTimeout } from '@/partials/el.Utils.js';
import 'tippy.js/dist/svg-arrow.css';
import 'tippy.js/dist/tippy.css';

elabs.Bootstrap = class {
  'use strict';

  constructor() {
    elabs.javascriptIsReady();
    this.initialiseImgLoadEffect();
    this.initialiseSectionAppearanceAnimations();
    this.initialiseScrollEvent();
    this.initialiseResizeEvent();
    this.initialiseSearch();
    elabs.Bootstrap.initialiseTooltips('[data-tippy-content]:not([data-tippy-in-slider])');
    this.fixIframeWidths();

    // TODO: delete this block once fully live, it is only used for dev process
    setTimeout(() => {
      // document.querySelector('.el-header__account-link')?.click();
      // document.querySelector('.el-header__menu-link')?.click();
      //document.querySelector('.el-footer__country-selector-button')?.click();
    }, 600);
  }

  // Set up an efficient scroll event listener to be used across the site
  initialiseScrollEvent() {
    let latestScrollY = 0;
    let ticking = false;

    const update = () => {
      ticking = false;
      const event = new CustomEvent('WINDOW_SCROLL_EVENT', {
        detail: {
          scrollY: latestScrollY,
        },
      });
      window.dispatchEvent(event);
    };

    const requestTick = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    window.addEventListener(
      'scroll',
      () => {
        latestScrollY = window.scrollY || window.pageYOffset;
        requestTick();
      },
      { passive: true },
    );
  }

  static initialiseTooltips(selector = '[data-tippy-content]') {
    const tippyInstances = [];

    document.querySelectorAll(selector).forEach((element) => {
      if (element._tippy) {
        return;
      }

      const isPositionFixed = element.getAttribute('data-tippy-positionfixed') === 'true';
      const triggerTarget = element.getAttribute('data-tippy-trigger-target');
      let triggerTargetClosest;
      if (!triggerTarget) {
        triggerTargetClosest = element.getAttribute('data-tippy-trigger-target-closest');
      }
      const showOnCreate = element.getAttribute('data-tippy-show-on-create') === 'true';
      const bounceDownwards = element.getAttribute('data-tippy-bounce-downwards') === 'true';
      const bounceUpwards = element.getAttribute('data-tippy-bounce-upwards') === 'true';
      const appendTo = element.getAttribute('data-tippy-append-to') === 'parent' ? element.parentNode : document.body;
      const maxWidth = parseInt(element.getAttribute('data-tippy-max-width'), 10) || 350;
      const disappearDelay = parseInt(element.getAttribute('data-tippy-disappear-delay'), 10);
      const shouldNotHideOnClick = element.getAttribute('data-tippy-hide-on-click') === 'false';
      const shouldHideAfterSeconds = element.getAttribute('data-tippy-hide-after-seconds');
      const hideAfter = Number(shouldHideAfterSeconds);
      const placementDesktop = element.getAttribute('data-tippy-placement-desktop');
      const placementMobile = element.getAttribute('data-tippy-placement-mobile');
      const placement = window.innerWidth >= 600 ? placementDesktop : placementMobile;

      const instance = tippy(element, {
        theme: 'sophiawebster',
        animation: 'shift-toward',
        showOnCreate,
        arrow: true,
        appendTo,
        maxWidth,
        interactiveBorder: 30,
        // delay: [null, 100000], // for DEBUGGING (delays the disappearance of the tooltip)
        ...(placement && { placement }),
        ...(shouldNotHideOnClick && { hideOnClick: false }),
        ...(disappearDelay && { delay: [null, disappearDelay] }),
        onShow(currentInstance) {
          if (!isNaN(hideAfter) && hideAfter > 0) {
            setTimeout(() => {
              instance.hide();
            }, hideAfter * 1000);
          }

          const parentSlide = instance.reference.closest('.tooltip-parent-slide');
          if (parentSlide) {
            let activeCount = parseInt(parentSlide.dataset.activeTooltips || '0', 10);
            activeCount++;
            parentSlide.dataset.activeTooltips = activeCount;
            parentSlide.classList.add('tooltip-active');
          }

          tippyInstances.forEach((i) => {
            if (i !== currentInstance) {
              i.hide();
            }
          });

          if (bounceUpwards) {
            currentInstance.popper.firstElementChild.classList.add('bouncing-upwards');
          } else if (bounceDownwards) {
            currentInstance.popper.firstElementChild.classList.add('bouncing-downwards');
          }
        },
        onHide(instance) {
          const parentSlide = instance.reference.closest('.tooltip-parent-slide');
          if (parentSlide) {
            let activeCount = parseInt(parentSlide.dataset.activeTooltips || '1', 10);
            activeCount--;
            parentSlide.dataset.activeTooltips = activeCount;

            if (activeCount <= 0) {
              parentSlide.classList.remove('tooltip-active');
            }
          }
        },
        ...(triggerTarget && { triggerTarget: document.querySelector(triggerTarget) }),
        ...(triggerTargetClosest && { triggerTarget: element.closest(triggerTargetClosest) }),
        ...(isPositionFixed && {
          popperOptions: {
            strategy: 'fixed',
          },
        }),
      });

      tippyInstances.push(...(Array.isArray(instance) ? instance : [instance]));
    });
  }

  initialiseResizeEvent() {
    let windowResizeAnimFrameRequested = false;

    const debouncedResize = debounce(() => {
      if (!windowResizeAnimFrameRequested) {
        windowResizeAnimFrameRequested = true;
        requestAnimationFrame(() => {
          windowResizeAnimFrameRequested = false;
          const event = new CustomEvent('WINDOW_RESIZED_EVENT', {
            detail: [window.innerWidth, window.innerHeight],
          });

          document.dispatchEvent(event);
        });
      }
    }, 1000);

    window.addEventListener('resize', debouncedResize);
  }

  // This handles search from the header, or side menu, or any other link to /search
  initialiseSearch = () => {
    document.addEventListener('click', (e) => {
      const target = e.target.closest('a'); // handles clicks on child elements inside <a>

      if (!target) {
        return;
      } // not a link

      if (target.getAttribute('href') === '/search') {
        e.preventDefault(); // stop navigation
        e.stopPropagation(); // stop bubbling
        const trigger = document.querySelector('.boost-search-overlay-hidden-trigger-element');
        if (!trigger) {
          return;
        }
        trigger.focus();
      }
    });
  };

  initialiseImgLoadEffect = () => {
    gsap.utils.toArray('.el-img-load-effect').forEach((img) => {
      ScrollTrigger.create({
        trigger: img,
        // start: 'top 60%', // element's top reaches 60% down viewport = 40% visible
        start: 'top 80%', // element's top reaches 80% down viewport = 20% visible
        toggleClass: { targets: img, className: 'is-in-viewport' },
        once: true, // only fire once (remove if you want it to toggle back/forth)
      });
    });

    document.querySelectorAll('.el-img-load-effect').forEach((img) => {
      if (img.complete) {
        img.classList.add('el-img-load-effect--loaded');
      } else {
        img.addEventListener('load', () => {
          img.classList.add('el-img-load-effect--loaded');
        });
      }
    });
  };

  initialiseSectionAppearanceAnimations = () => {
    ScrollTrigger.create({
      trigger: '.el-footer__bottom-bar',
      start: 'top bottom', // when footer enters viewport
      end: 'bottom top', // until footer fully leaves
      onEnter: () => document.body.classList.add('el-footer-is-visible'),
      onEnterBack: () => document.body.classList.add('el-footer-is-visible'),
      onLeave: () => document.body.classList.remove('el-footer-is-visible'),
      onLeaveBack: () => document.body.classList.remove('el-footer-is-visible'),
    });

    requestTimeout(() => {
      ScrollTrigger.refresh();
    }, 2000);

    requestTimeout(() => {
      ScrollTrigger.refresh();
    }, 5000);

    document.addEventListener('WINDOW_RESIZED_EVENT', () => {
      ScrollTrigger.refresh();
    });

    gsap.utils.toArray('.el-slideshow-container').forEach((el) => {
      ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onEnter: (self) => self.trigger.classList.add('in-view'),
        onLeaveBack: (self) => self.trigger.classList.remove('in-view'),
      });
    });

    gsap.utils.toArray('.el-column-title').forEach((el) => {
      const taglineSplit = new SplitText(el, { type: 'chars' });
      gsap.set(taglineSplit.chars, { yPercent: -2, opacity: 0 });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: el,
            start: 'bottom bottom',
            end: 'top top',
            toggleActions: 'play none none reset',
          },
        })
        .fromTo(
          taglineSplit.chars,
          {
            rotationZ: -30,
            yPercent: -45,
          },
          {
            yPercent: 0,
            opacity: 1,
            stagger: 0.06,
            duration: 1.4,
            delay: 0.2,
            rotationZ: 0,
            ease: 'expo.out',
          },
        );
    });
  };

  fixIframeWidths = () => {
    document.querySelectorAll('.text-block iframe').forEach((iframe) => {
      const w = iframe.getAttribute('width');
      const h = iframe.getAttribute('height');

      if (w && h) {
        // Apply responsive styles, maintaining aspect ratio (and making sure it doesn't go wider than its container)
        iframe.style.width = '100%';
        iframe.style.height = 'auto';
        iframe.style.maxWidth = '100%';
        iframe.style.aspectRatio = `${w} / ${h}`;
      }
    });
  };
};

document.addEventListener('DOMContentLoaded', () => {
  elabs.bootstrap = new elabs.Bootstrap();
});
